import React, { useEffect, useState } from 'react';
import api from '../lib/api';
import { useAuth } from '../lib/auth';
import { fmt, fmtDate, fmtDateShort } from '../lib/format';
import { CurrencyCircleDollar, ArrowsClockwise, TrendUp, TrendDown, Minus, ChartLine } from '@phosphor-icons/react';

export default function RatePage() {
  const { rate, setRate } = useAuth();
  const [newRate, setNewRate] = useState('');
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState(false);
  const [history, setHistory] = useState([]);

  const loadHistory = async () => {
    const r = await api.get('/rate/history'); setHistory(r.data);
  };

  useEffect(() => { if (rate) setNewRate(rate.cost_rate); }, [rate]);
  useEffect(() => { loadHistory(); }, []);

  const save = async () => {
    setSaving(true); setSuccess(false);
    try {
      const r = await api.put('/rate', { cost_rate: parseFloat(newRate) });
      setRate(r.data);
      setSuccess(true);
      loadHistory();
      setTimeout(() => setSuccess(false), 3000);
    } finally { setSaving(false); }
  };

  // Build trend chart data (last 30 entries reversed for chronological)
  const chart = [...history].reverse();
  const rates = chart.map(h => h.rate);
  const minR = rates.length ? Math.min(...rates) : 0;
  const maxR = rates.length ? Math.max(...rates) : 1;
  const range = maxR - minR || 0.01;

  return (
    <div className="space-y-5 fade-in max-w-3xl" data-testid="rate-page">
      <div>
        <h1 className="text-2xl font-semibold text-white tracking-tight">汇率管理</h1>
        <p className="text-sm text-slate-400 mt-0.5">每天更新成本汇率 — 员工录单自动带出 · 已录订单保留当时汇率不变</p>
      </div>

      <div className="card-base p-6 sm:p-8">
        <div className="flex items-center gap-3 mb-5">
          <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-indigo-500/20 to-violet-500/10 border border-indigo-500/30 flex items-center justify-center">
            <CurrencyCircleDollar size={24} weight="duotone" className="text-indigo-300" />
          </div>
          <div>
            <div className="text-lg font-semibold text-white">MYR → USDT 成本汇率</div>
            <div className="text-xs text-slate-500">
              {rate && <>更新于 {fmtDate(rate.updated_at)} · by {rate.updated_by}</>}
            </div>
          </div>
        </div>

        <div className="bg-slate-900/60 border border-slate-700 rounded-xl p-5 mb-5">
          <div className="label-tiny mb-2">当前汇率</div>
          <div className="font-num text-4xl font-bold text-white">
            1 USDT = <span className="text-indigo-300">{rate?.cost_rate || '-'}</span> <span className="text-xl text-slate-500">MYR</span>
          </div>
        </div>

        <div>
          <label className="label-tiny block mb-2">新汇率(今日成本)</label>
          <input
            type="number"
            step="0.0001"
            min="0"
            className="input-base font-num text-2xl"
            value={newRate}
            onChange={e => setNewRate(e.target.value)}
            data-testid="rate-input"
          />
          <p className="text-xs text-slate-500 mt-2">
            ⚠️ 改这个不会影响已经录的订单(每笔订单永久保留当时的成本汇率)。只影响今天之后录的新单和员工卖出汇率的计算参考。
          </p>
        </div>

        <button onClick={save} disabled={saving || !newRate} className="btn-primary w-full mt-5 py-3 inline-flex items-center justify-center gap-2" data-testid="save-rate">
          <ArrowsClockwise size={16} /> {saving ? '更新中…' : '更新汇率并通知所有员工'}
        </button>

        {success && (
          <div className="mt-4 text-sm text-emerald-300 bg-emerald-500/10 border border-emerald-500/20 rounded-lg p-3 text-center">
            ✓ 汇率已更新,所有员工 30 秒内会收到通知
          </div>
        )}
      </div>

      {/* Trend chart */}
      {chart.length >= 2 && (
        <div className="card-base p-5 sm:p-6">
          <h2 className="text-lg font-semibold text-white mb-1 flex items-center gap-2">
            <ChartLine size={20} weight="duotone" className="text-indigo-400" /> 汇率趋势
          </h2>
          <p className="text-xs text-slate-500 mb-4">最近 {chart.length} 次变更 · 区间 {fmt(minR, 4)} ~ {fmt(maxR, 4)}</p>
          <SparkChart points={rates} min={minR} max={maxR} />
        </div>
      )}

      {/* History */}
      <div className="card-base p-5 sm:p-6">
        <h2 className="text-lg font-semibold text-white mb-4">变更历史</h2>
        {history.length === 0 && (
          <div className="text-sm text-slate-500 text-center py-6">还没有变更记录,每次改汇率会自动记录</div>
        )}
        {history.length > 0 && (
          <div className="space-y-2">
            {history.map((h, i) => {
              const isLatest = i === 0;
              const diff = h.previous_rate !== null && h.previous_rate !== undefined
                ? h.rate - h.previous_rate : null;
              return (
                <div key={h.id} className={`p-3 rounded-lg border ${isLatest ? 'border-indigo-500/30 bg-indigo-500/5' : 'border-slate-800 bg-slate-900/40'}`}>
                  <div className="flex items-center justify-between gap-3">
                    <div className="flex items-center gap-3 min-w-0">
                      <TrendBadge diff={diff} />
                      <div className="min-w-0">
                        <div className="font-num text-base text-white font-semibold">
                          {fmt(h.rate, 4)}
                          {h.previous_rate !== null && h.previous_rate !== undefined && (
                            <span className="text-slate-500 text-xs ml-2 font-normal">
                              ← 之前 {fmt(h.previous_rate, 4)}
                            </span>
                          )}
                        </div>
                        <div className="text-xs text-slate-500 font-num">
                          {fmtDateShort(h.changed_at)} · by {h.changed_by}
                          {isLatest && <span className="ml-2 text-indigo-400">· 当前</span>}
                        </div>
                      </div>
                    </div>
                    {diff !== null && (
                      <div className={`font-num text-sm font-semibold ${
                        diff > 0 ? 'text-rose-400' : diff < 0 ? 'text-emerald-400' : 'text-slate-400'
                      }`}>
                        {diff > 0 ? '+' : ''}{fmt(diff, 4)}
                      </div>
                    )}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="card-base p-5 bg-indigo-500/5 border-indigo-500/20">
        <div className="text-sm text-slate-300 space-y-2">
          <div className="font-semibold text-white">💡 关于汇率波动</div>
          <p className="text-xs leading-relaxed text-slate-400">
            ✅ 每笔订单录入时,系统会**永久记录当时的成本汇率**,后续改汇率不会影响已录订单的利润计算。<br/>
            ✅ 月度报表的「USDT 折算 MYR」会用 <strong className="text-indigo-300">该月所有 MYR 订单的加权平均汇率</strong>(不再固定 4.10),更准确反映真实盈利。<br/>
            💎 你的 USDT 利润数字始终是精确的(因为每单都按当时汇率算),只有 MYR 折算是估算值。
          </p>
        </div>
      </div>
    </div>
  );
}

function TrendBadge({ diff }) {
  if (diff === null || diff === undefined) {
    return (
      <div className="w-9 h-9 rounded-lg bg-slate-700/40 border border-slate-700 flex items-center justify-center">
        <Minus size={16} weight="bold" className="text-slate-400" />
      </div>
    );
  }
  if (diff > 0) {
    return (
      <div className="w-9 h-9 rounded-lg bg-rose-500/15 border border-rose-500/30 flex items-center justify-center">
        <TrendUp size={16} weight="bold" className="text-rose-400" />
      </div>
    );
  }
  if (diff < 0) {
    return (
      <div className="w-9 h-9 rounded-lg bg-emerald-500/15 border border-emerald-500/30 flex items-center justify-center">
        <TrendDown size={16} weight="bold" className="text-emerald-400" />
      </div>
    );
  }
  return (
    <div className="w-9 h-9 rounded-lg bg-slate-700/40 border border-slate-700 flex items-center justify-center">
      <Minus size={16} weight="bold" className="text-slate-400" />
    </div>
  );
}

function SparkChart({ points, min, max }) {
  if (points.length < 2) return null;
  const w = 800, h = 140, pad = 8;
  const range = (max - min) || 1;
  const stepX = (w - pad * 2) / (points.length - 1);
  const ptCoords = points.map((p, i) => {
    const x = pad + i * stepX;
    const y = h - pad - ((p - min) / range) * (h - pad * 2);
    return [x, y];
  });
  const path = ptCoords.map((c, i) => (i === 0 ? 'M' : 'L') + c[0] + ',' + c[1]).join(' ');
  const areaPath = path + ` L${pad + (points.length - 1) * stepX},${h - pad} L${pad},${h - pad} Z`;

  return (
    <div className="bg-slate-900/40 rounded-lg p-3 border border-slate-800">
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full h-32" preserveAspectRatio="none">
        <defs>
          <linearGradient id="grad" x1="0" y1="0" x2="0" y2="1">
            <stop offset="0%" stopColor="#6366F1" stopOpacity="0.4" />
            <stop offset="100%" stopColor="#6366F1" stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={areaPath} fill="url(#grad)" />
        <path d={path} fill="none" stroke="#818CF8" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {ptCoords.map((c, i) => (
          <circle key={`pt-${c[0]}-${c[1]}-${i}`} cx={c[0]} cy={c[1]} r="3" fill="#A5B4FC" stroke="#0B1121" strokeWidth="2" />
        ))}
      </svg>
      <div className="flex justify-between text-xs text-slate-500 font-num mt-1 px-1">
        <span>{fmt(points[0], 4)}</span>
        <span className="text-slate-400">→</span>
        <span>{fmt(points[points.length - 1], 4)}</span>
      </div>
    </div>
  );
}
